import { type FC, useCallback, useMemo, useState } from 'react'

import { Trigger } from '@radix-ui/react-select'

import ChevronDownIcon from '@/assets/icons/icon-chevron-down.svg'
import { Select, SelectContent, SelectGroup } from '@/components/ui/select'
import { CountrySelectItem } from '@/modules/chatting/ui/chat-window/fan-info/actions/EditCountry/CountrySelectItem'
import { IconButton } from '@/modules/chatting/ui/components/icon-button'
import { classNames } from '@/utils/helpers'

import { SearchInput } from '../search-input'
import { Separator } from '../separator'
import { COUNTRY_OPTIONS, getCountryNameByCode } from './constants'

interface Props {
  value?: string
  onChange: (value: string | null) => void
  testId?: string
}

export const CountrySelect: FC<Props> = ({ value, onChange, testId }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')

  const handleCountrySelect = useCallback(
    async (country: string | null) => {
      setIsOpen(false)
      onChange(country)
    },
    [onChange],
  )

  const countries = useMemo(() => {
    return COUNTRY_OPTIONS.filter(
      (country) =>
        country.name.toLowerCase().includes(search.toLowerCase()) ||
        country.code.toLowerCase().includes(search.toLowerCase()),
    )
  }, [search])

  const countryName = value ? getCountryNameByCode(value) : 'Select a Country'

  return (
    <Select
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Trigger
        className="bg-card focus-within:border-primary-500 focus:border-primary-500 group relative flex h-10 w-full flex-grow items-center justify-between gap-2 rounded-md border px-3 py-2 font-medium"
        data-testid={testId}
      >
        {countryName}
        <IconButton
          icon={ChevronDownIcon}
          iconClassName={classNames(
            'transition-transform',
            isOpen && '-rotate-180 text-primary-500',
          )}
          className="text-primary-500 h-5 w-5"
          tooltip={value || 'Select a Country'}
        />
      </Trigger>
      <SelectContent className="overflow-hidden">
        <SearchInput
          placeholder="Search"
          className="w-full border-none"
          value={search}
          onInputChange={setSearch}
          testId={`${testId}-search-input`}
        />
        <Separator className="mb-2" />
        <SelectGroup className="visible-scrollbar-track max-h-[200px] overflow-auto">
          {!!countries.length &&
            countries.map(({ name, code }) => (
              <CountrySelectItem
                key={code}
                selected={value === code}
                openByDefault={!!search}
                value={code}
                label={name}
                onClick={handleCountrySelect}
                testId={`${testId}-select-item-${code}`}
              />
            ))}
          {!countries.length && (
            <div className="text-title pointer-events-none py-2 text-center text-sm font-medium leading-[22px]">
              No results.
            </div>
          )}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}
